import React, { useState, useEffect } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { GoAlertFill } from "react-icons/go";
import Loader from "../../Global/Loader";
import axios from "axios";

function ChangeEmail() {
  const [email, setEmail] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    setIsValid(emailRegex.test(email) || email === "");
  }, [email]);

  useGSAP(() => {
    const fields = document.querySelectorAll(".relative");

    fields.forEach((field) => {
      const input = field.querySelector("input, select");
      const label = field.querySelector("label");
      const pLabel = field.querySelector("p");
      const cancelButton = field.querySelector(".cancel-button");

      if (input) {
        input.addEventListener("focus", () => {
          gsap.to(label, {
            top: -10,
            fontSize: "0.75rem",
            color: "#22c55e",
            duration: 0.3,
          });
          gsap.to(input, { borderColor: "#22c55e", duration: 0.3 });
          pLabel.classList.remove("text-red-500");
          pLabel.classList.add("text-gray-500");
        });

        input.addEventListener("blur", () => {
          if (!input.value.trim()) {
            gsap.to(label, {
              top: 10,
              fontSize: "1rem",
              color: "#4b5563",
              duration: 0.3,
            });
          }
          gsap.to(input, { borderColor: "#d1d5db", duration: 0.3 });
        });

        if (cancelButton) {
          cancelButton.addEventListener("click", () => {
            gsap.to(label, {
              top: 10,
              fontSize: "1rem",
              color: "#4b5563",
              duration: 0.3,
            });
            gsap.to(input, { borderColor: "#d1d5db", duration: 0.3 });
            input.value = ""; // Reset input value
            setEmail(""); // Clear email state
          });
        }
      }
    });
  }, []);

  const handleLogout = () => {
    // Clear user session data
    localStorage.clear();
    sessionStorage.clear();

    // Replace the history state to prevent back navigation
    window.history.replaceState(null, null, "/signin");

    // Forcefully reload the page to clear all states
    setTimeout(() => {
      window.location.href = "/signin";
    }, 100); // Slight delay to ensure changes take effect
  };

  const handleSubmit = async () => {
    if (!emailRegex.test(email)) {
      alert("Please enter a valid email address.");
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/auth/change-email`,
        {
          newEmail: email,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      handleLogout();
    } catch (error) {
      setError(
        error.response?.data?.message ||
          "An unexpected error occurred. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useGSAP(() => {
    const fields = document.querySelectorAll(".relative");

    fields.forEach((field) => {
      const input = field.querySelector("input, select");
      const label = field.querySelector("label");
      const errorSpan = field.querySelector("span"); // Using <span> instead of <p>
      const cancelButton = field.querySelector(".cancel-button");

      if (input) {
        // Focus animation
        input.addEventListener("focus", () => {
          gsap.to(label, {
            top: -10,
            fontSize: "0.75rem",
            color: "#22c55e",
            duration: 0.25,
          }); // Updated transition time
          gsap.to(input, { borderColor: "#22c55e", duration: 0.25 });
          if (errorSpan) {
            errorSpan.classList.remove("text-red-500");
            errorSpan.classList.add("text-gray-500");
          }
        });

        // Blur animation
        input.addEventListener("blur", () => {
          if (!input.value.trim()) {
            gsap.to(label, {
              top: 10,
              fontSize: "1rem",
              color: "#4b5563",
              duration: 0.25,
            }); // Updated transition time
          }
          gsap.to(input, { borderColor: "#d1d5db", duration: 0.25 });
        });

        // Cancel Button Animation
        if (cancelButton) {
          cancelButton.addEventListener("click", () => {
            gsap.to(label, {
              top: 10,
              fontSize: "1rem",
              color: "#4b5563",
              duration: 0.25,
            });
            gsap.to(input, { borderColor: "#d1d5db", duration: 0.25 });
            input.value = ""; // Reset input value
            setEmail(""); // Clear email state
          });
        }
      }
    });
  }, []);

  return (
    <div className="flex flex-col space-y-6 w-[72%]">
      {isSubmitting && <Loader />}
      {/* Existing Right Card */}
      <div className="px-10 py-6 bg-white rounded shadow-lg">
        {/* Change Email */}
        <h2 className="text-gray-800 text-lg font-semibold mb-4">
          Change Email
          <span className="text-gray-500 text-base font-thin pl-2">
            change your account email
          </span>
        </h2>
        <hr className="border-t border-gray-300 mb-4" />

        {error ? (
          <div className="flex items-center gap-x-2 bg-red-100 border border-red-300 text-red-700 text-sm font-semibold px-4 py-4 rounded-md mb-4">
            <GoAlertFill className="size-5" />
            <p>{error}</p>
          </div>
        ) : (
          <div className="flex items-start bg-blue-100 border border-blue-300 text-blue-700 px-4 py-3 rounded mt-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 flex-shrink-0 mr-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M12 20h.01M12 20h-2a2 2 0 01-2-2V6a2 2 0 012-2h4a2 2 0 012 2v12a2 2 0 01-2 2h-2zm0-18a2 2 0 00-2-2v.01a2 2 0 002 2zm0 4a2 2 0 00-2-2v.01a2 2 0 002 2z"
              />
            </svg>
            <div>
              <span className="text-sm">
                Once your email is updated, you will be logged out of Portal!
              </span>
              <span className="text-sm">
                {" "}
                You will need to Sign In back with the updated email.
              </span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 mt-6">
          {/* Email */}
          <div className="relative">
            <label
              htmlFor="email"
              className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1"
            >
              Primary Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full p-2 border rounded ${
                !isValid && email !== "" ? "border-red-500" : ""
              }`}
            />
            <p className={`text-xs mt-1 text-gray-500`}>
              Please enter email in correct format e.g: username@provider.com
            </p>
          </div>
        </div>

        <div className="flex justify-start items-start space-x-4 mt-10 mr-5">
          {/* Save & Close and Close buttons */}
          <button
            onClick={handleSubmit}
            disabled={!isValid || email === ""}
            className={`px-4 py-2 rounded border ${
              isValid && email
                ? "bg-white text-blue-500 border-blue-500 hover:bg-blue-500 hover:text-white cursor-pointer"
                : "bg-white text-blue-200 border-blue-200 cursor-not-allowed"
            }`}
          >
            Change Email
          </button>
          <button
            onClick={() => setEmail("")}
            className="cancel-button bg-white text-gray-500 border border-gray-500 px-4 py-2 rounded hover:bg-gray-500 hover:text-white"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChangeEmail;
